// GeneratedImagesStrip — a compact "Generated images" section that appears above
// the composer when async image-generation jobs complete for this training.
//
// It listens (via useSessionImageJobs) to the SAME backend `session_job_*` socket
// events the async Activity widget uses — over the chat's own independent socket
// connection — and renders each completed image as a small card with an
// "Add to scene" button. The button commits the image into the current scene via
// the existing `addImageToScene(imageUrl)` from ChatCommitContext (the factory
// `makeAddImageToScene(widgetProps)` ChatPanel already provides). No new commit
// path; we reuse the proven one.
//
// Unobtrusive by design: renders nothing when there are no items. Styling follows
// the chat scoping contract — every utility is `tw-`-prefixed and only takes
// effect under `.aidea-chat-root`; brand tokens via CSS vars. No emoji.

import React, { useCallback, useState } from "react";

import useSessionImageJobs from "./useSessionImageJobs";
import { approveJob } from "../../../common/server_utils.es6";
import { useChatCommit } from "./ChatCommitContext";
import { ImageIcon, PlusIcon, CheckIcon, CloseIcon, SpinnerIcon, AlertIcon } from "./icons";

// Per-card add state: idle → adding → added | error. Drives the action button.
function AddButton({ state, onAdd }) {
  if (state === "added") {
    return (
      <span className="tw-inline-flex tw-flex-none tw-items-center tw-gap-1 tw-rounded-lg tw-bg-[var(--aidea-primary-tint)] tw-px-2.5 tw-py-1.5 tw-text-[12px] tw-font-medium tw-text-[var(--aidea-primary)]">
        <CheckIcon size={13} />
        Added
      </span>
    );
  }

  const isAdding = state === "adding";
  const isError = state === "error";
  return (
    <button
      type="button"
      onClick={onAdd}
      disabled={isAdding}
      title={isError ? "Couldn’t add — try again" : "Add to scene"}
      className={`tw-inline-flex tw-flex-none tw-items-center tw-gap-1 tw-rounded-full tw-px-2.5 tw-py-1.5 tw-text-[12px] tw-font-medium tw-transition-colors tw-duration-200 tw-cursor-pointer disabled:tw-cursor-not-allowed disabled:tw-opacity-60 focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[var(--aidea-primary)] focus-visible:tw-ring-offset-1 ${
        isError
          ? "tw-bg-[var(--aidea-muted-bg)] tw-text-[var(--aidea-fg)] tw-border tw-border-[var(--aidea-border)] hover:tw-border-[var(--aidea-primary)]"
          : "tw-bg-[var(--ac-ink)] tw-text-[var(--aidea-on-primary)] hover:tw-opacity-90"
      }`}
    >
      {isAdding ? (
        <SpinnerIcon size={13} className="aidea-spin" />
      ) : isError ? (
        <AlertIcon size={13} />
      ) : (
        <PlusIcon size={13} />
      )}
      {isAdding ? "Adding…" : isError ? "Couldn’t add" : "Add to scene"}
    </button>
  );
}

// One generated-image card: thumbnail + (prompt) + add-to-scene + dismiss.
function ImageItem({ item, onDismiss }) {
  const { addImageToScene } = useChatCommit();
  const [state, setState] = useState("idle"); // idle | adding | added | error

  const handleAdd = useCallback(async () => {
    if (typeof addImageToScene !== "function") {
      setState("error");
      return;
    }
    setState("adding");
    try {
      await addImageToScene(item.imageUrl);
      setState("added");
      // Sahneye eklendi → widget'taki "Onay Bekliyor" kartını kapat.
      approveJob(item.jobId);
    } catch {
      // Surface a recoverable error; the button reverts to a retry affordance.
      setState("error");
    }
  }, [addImageToScene, item.imageUrl, item.jobId]);

  const generating = item.status === "generating";

  return (
    <div className="tw-flex tw-items-center tw-gap-2.5 aidea-chat-card tw-border tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-surface)] tw-p-2">
      <div className="tw-relative tw-h-12 tw-w-12 tw-flex-none tw-overflow-hidden tw-rounded-lg tw-bg-[var(--aidea-muted-bg)]">
        {item.imageUrl ? (
          // eslint-disable-next-line jsx-a11y/img-redundant-alt
          <img
            src={item.imageUrl}
            alt={item.prompt ? `Generated image: ${item.prompt}` : "Generated image"}
            className="tw-h-full tw-w-full tw-object-cover"
            loading="lazy"
          />
        ) : (
          <span className="tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center tw-text-[var(--aidea-muted-fg)]">
            <ImageIcon size={16} />
          </span>
        )}
        {generating && (
          <span className="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white/60">
            <SpinnerIcon size={14} className="aidea-spin tw-text-[var(--aidea-primary)]" />
          </span>
        )}
      </div>

      <div className="tw-min-w-0 tw-flex-1">
        <p className="tw-m-0 tw-truncate tw-text-[12.5px] tw-font-medium tw-text-[var(--aidea-fg)]">
          {generating ? "Generating image…" : item.prompt || "Generated image"}
        </p>
        {!generating && item.prompt && (
          <p className="tw-m-0 tw-mt-0.5 tw-truncate tw-text-[11px] tw-text-[var(--aidea-muted-fg)]">
            Ready to add to your scene
          </p>
        )}
      </div>

      {!generating && <AddButton state={state} onAdd={handleAdd} />}

      <button
        type="button"
        aria-label="Dismiss"
        title="Dismiss"
        onClick={onDismiss}
        className="tw-flex tw-h-7 tw-w-7 tw-flex-none tw-items-center tw-justify-center tw-rounded-lg tw-text-[var(--aidea-muted-fg)] tw-transition-colors tw-duration-200 tw-cursor-pointer hover:tw-bg-[var(--aidea-muted-bg)] hover:tw-text-[var(--aidea-fg)] focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-[var(--aidea-primary)]"
      >
        <CloseIcon size={14} />
      </button>
    </div>
  );
}

// The strip. Renders nothing unless there are (non-dismissed) image jobs. Sits
// above the composer in the chat thread.
export default function GeneratedImagesStrip() {
  const { items } = useSessionImageJobs();
  // Locally dismissed item keys (jobId::imageUrl). Dismissal is view-only — it
  // does not touch the job/backend (the Activity widget is the source of truth).
  const [dismissed, setDismissed] = useState(() => new Set());

  const visible = items.filter((it) => !dismissed.has(`${it.jobId}::${it.imageUrl}`));
  if (visible.length === 0) return null;

  return (
    <section
      aria-label="Generated images"
      className="tw-flex-none tw-border-t tw-border-[var(--aidea-border)] tw-bg-[var(--aidea-muted-bg)] tw-px-2.5 tw-py-2"
    >
      <div className="tw-mb-1.5 tw-flex tw-items-center tw-gap-1.5 tw-px-0.5">
        <ImageIcon size={13} className="tw-flex-none tw-text-[var(--aidea-primary)]" />
        <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-text-[var(--aidea-muted-fg)]">
          Generated images
        </span>
      </div>
      <div className="tw-flex tw-max-h-40 tw-flex-col tw-gap-1.5 tw-overflow-y-auto aidea-chat-scroll">
        {visible.map((it) => {
          const key = `${it.jobId}::${it.imageUrl}`;
          return (
            <ImageItem
              key={key}
              item={it}
              onDismiss={() => setDismissed((prev) => new Set(prev).add(key))}
            />
          );
        })}
      </div>
    </section>
  );
}
